import { Icon } from '@iconify-icon/react';
import Link from 'next/link';
import { NextSeo } from 'next-seo';

import { DocsArticleLayout } from '@/components/modules/Docs';
import PageLink from '@/utils/PageLink';

const tableOfContents = [
  { id: 'when-multiple-bots', title: '1. When to Create Multiple Bots' },
  { id: 'plan-limits', title: '2. Plan Limits & Scaling' },
  { id: 'inviting-staff', title: '3. Inviting Staff Members' },
  { id: 'assigning-bots', title: '4. Assigning Bots to Team Members' },
  { id: 'permissions', title: '5. Setting Granular Permissions' },
  { id: 'bulk-operations', title: '6. Bulk Operations' },
  { id: 'organization-strategies', title: '7. Organization Strategies' },
];

const MultiBotOrchestrationPage = () => {
  return (
    <>
      <NextSeo
        title="Managing Multiple Bots & Teams"
        description="Learn how to manage multiple chatbots, invite team members, assign permissions, and organize your AI assistants across departments."
        canonical="https://chat.co/docs/multi-bot-orchestration"
        openGraph={{
          url: 'https://chat.co/docs/multi-bot-orchestration',
          title: 'Managing Multiple Bots & Teams | Chat.co',
          description:
            'Learn how to manage multiple chatbots, invite team members, assign permissions, and organize your AI assistants across departments.',
          type: 'article',
          article: {
            publishedTime: '2026-01-01T00:00:00Z',
            modifiedTime: '2026-01-26T00:00:00Z',
            section: 'Tutorials',
            tags: ['multi-bot', 'team management', 'permissions', 'enterprise'],
          },
          images: [
            {
              url: 'https://chat.co/images/og-docs.png',
              width: 1200,
              height: 630,
              alt: 'Chat.co - Multi-Bot Management',
            },
          ],
        }}
        twitter={{
          cardType: 'summary_large_image',
        }}
      />

      <DocsArticleLayout
        category="Tutorials"
        categoryColor="text-blue-700"
        title="Managing Multiple Bots & Teams"
        description="Scale your AI support with multiple chatbots, team collaboration, and granular access control."
        readingTime="12 min read"
        lastUpdated="January 2026"
        tableOfContents={tableOfContents}
        prevArticle={{
          title: 'Private-App Chatbot with MCP',
          href: PageLink.docsPrivateAppChatbotMcp,
        }}
        nextArticle={{
          title: 'API Documentation',
          href: PageLink.docsApiReference,
        }}
        keyTakeaways={[
          'Create specialized bots for different departments or use cases',
          'Invite team members and assign them to specific chatbots',
          'Granular permissions control who can edit, train, or deploy',
          'Bulk operations let you manage multiple bots efficiently',
          'Organize by department, product, or audience for clarity',
        ]}>
        {/* Introduction */}
        <p className="lead text-lg text-gray-700">
          As your organization grows, a single chatbot may not be enough. Different departments have
          different knowledge bases, different teams need different access levels, and different
          audiences need different experiences. This guide shows you how to manage multiple bots and
          collaborate with your team effectively.
        </p>

        {/* Section 1: When to Create Multiple Bots */}
        <section id="when-multiple-bots" className="scroll-mt-24">
          <h2>1. When to Create Multiple Bots</h2>

          <p>
            While it&apos;s tempting to put everything into one chatbot, there are clear scenarios
            where multiple bots work better.
          </p>

          <h3>Signs You Need Multiple Bots</h3>
          <div className="my-6 space-y-3">
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100">
                <Icon icon="tabler:building" className="text-lg text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Different departments</h4>
                <p className="mt-1 text-sm text-gray-600">
                  HR, Sales, Support, and IT have distinct knowledge bases and should have separate
                  bots to avoid confusion and maintain accuracy.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-green-100">
                <Icon icon="tabler:users" className="text-lg text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Different audiences</h4>
                <p className="mt-1 text-sm text-gray-600">
                  Internal employees vs. external customers need different information, tone, and
                  access levels.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-purple-100">
                <Icon icon="tabler:world" className="text-lg text-purple-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Different products or brands</h4>
                <p className="mt-1 text-sm text-gray-600">
                  Each product line or brand may need its own bot with specific branding, knowledge,
                  and personality.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-amber-100">
                <Icon icon="tabler:lock" className="text-lg text-amber-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Confidentiality requirements</h4>
                <p className="mt-1 text-sm text-gray-600">
                  Sensitive information (financial, legal, HR) should be isolated in private bots
                  with restricted access.
                </p>
              </div>
            </div>
          </div>

          <h3>When One Bot is Enough</h3>
          <p>Stick with a single bot if:</p>
          <ul>
            <li>All your content is related and serves the same audience</li>
            <li>Your knowledge base is small (under 50 documents)</li>
            <li>You don&apos;t need team collaboration or access control</li>
            <li>You&apos;re just getting started and want to keep things simple</li>
          </ul>

          <div className="my-6 rounded-lg border-l-4 border-blue-500 bg-blue-50 p-4">
            <p className="text-sm text-blue-900">
              <strong>Tip:</strong> Start with one bot and split later. It&apos;s easier to divide
              an existing bot into specialized ones than to merge separate bots.
            </p>
          </div>
        </section>

        {/* Section 2: Plan Limits & Scaling */}
        <section id="plan-limits" className="scroll-mt-24">
          <h2>2. Plan Limits & Scaling</h2>

          <p>
            Starter includes up to two chatbots total, each public or private. Chatbot and
            team-member limits depend on your plan.
          </p>

          <h3>Chatbot Limits by Plan</h3>
          <div className="my-6 overflow-x-auto">
            <table className="min-w-full border border-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Plan
                  </th>
                  <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Chatbots
                  </th>
                  <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Expert Bots
                  </th>
                  <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Team Seats
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    Starter
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">2</td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">0</td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">1</td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    Pro
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">3</td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">3</td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">1</td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    Business
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">10</td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">5</td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">10</td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    Enterprise
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">Unlimited</td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">Unlimited</td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">Unlimited</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3>Scaling with Add-ons</h3>
          <p>Need more capacity? Add-ons let you grow without changing plans:</p>
          <div className="my-6 grid gap-4 sm:grid-cols-2">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-center gap-2">
                <Icon icon="tabler:robot" className="text-xl text-primary-500" />
                <span className="font-semibold text-gray-900">Extra Chatbot</span>
              </div>
              <p className="mt-2 text-sm text-gray-600">+1 chatbot slot</p>
              <p className="mt-1 text-lg font-bold text-gray-900">$20/month</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-center gap-2">
                <Icon icon="tabler:user-plus" className="text-xl text-primary-500" />
                <span className="font-semibold text-gray-900">Team Seat</span>
              </div>
              <p className="mt-2 text-sm text-gray-600">+1 team member slot</p>
              <p className="mt-1 text-lg font-bold text-gray-900">$5/month</p>
            </div>
          </div>

          <div className="my-6 rounded-lg border-l-4 border-amber-500 bg-amber-50 p-4">
            <p className="text-sm text-amber-900">
              <strong>Note:</strong> If you downgrade your plan and exceed the new limits, excess
              bots will be frozen (not deleted). You&apos;ll have 30 days to upgrade or choose which
              bots to keep active.
            </p>
          </div>
        </section>

        {/* Section 3: Inviting Staff Members */}
        <section id="inviting-staff" className="scroll-mt-24">
          <h2>3. Inviting Staff Members</h2>

          <p>
            Team collaboration starts with inviting staff members to your account. Staff members can
            help manage bots without having full account access.
          </p>

          <h3>How to Invite Staff</h3>
          <ol>
            <li>
              <strong>Go to Settings → Staff Management</strong>
              <p className="mt-1 text-gray-600">
                From your dashboard, click your profile icon and select{' '}
                <code>Staff Management</code>.
              </p>
            </li>
            <li>
              <strong>Click &quot;Invite Staff Member&quot;</strong>
              <p className="mt-1 text-gray-600">
                Enter the email address of the person you want to invite.
              </p>
            </li>
            <li>
              <strong>Select initial bot assignments (optional)</strong>
              <p className="mt-1 text-gray-600">
                You can assign bots now or do it later after they accept.
              </p>
            </li>
            <li>
              <strong>Send invitation</strong>
              <p className="mt-1 text-gray-600">
                They&apos;ll receive an email with a link to join your team.
              </p>
            </li>
          </ol>

          <h3>Staff Member Roles</h3>
          <div className="my-6 space-y-4">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100">
                  <Icon icon="tabler:crown" className="text-lg text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Account Owner</h4>
                  <p className="mt-1 text-sm text-gray-600">
                    Full access to everything. Manages billing, staff, and all chatbots.
                    There&apos;s only one owner per account.
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-purple-100">
                  <Icon icon="tabler:shield" className="text-lg text-purple-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Admin</h4>
                  <p className="mt-1 text-sm text-gray-600">
                    Elevated staff member who can manage other staff and bots on behalf of the
                    owner. Cannot access billing.
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-green-100">
                  <Icon icon="tabler:user" className="text-lg text-green-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Team Member</h4>
                  <p className="mt-1 text-sm text-gray-600">
                    Can only access assigned bots with specific permissions. Cannot see other bots
                    or manage staff.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <h3>Managing Staff</h3>
          <p>From the Staff Management page, you can:</p>
          <ul>
            <li>
              <strong>View all team members</strong> — See who has access and their status
            </li>
            <li>
              <strong>Edit assignments</strong> — Change which bots a staff member can access
            </li>
            <li>
              <strong>Remove staff</strong> — Revoke access (they lose all bot assignments)
            </li>
            <li>
              <strong>Resend invitations</strong> — For pending invites that weren&apos;t accepted
            </li>
          </ul>
        </section>

        {/* Section 4: Assigning Bots to Team Members */}
        <section id="assigning-bots" className="scroll-mt-24">
          <h2>4. Assigning Bots to Team Members</h2>

          <p>
            Staff members only see bots they&apos;re assigned to. This keeps things organized and
            secure.
          </p>

          <h3>How to Assign Bots</h3>
          <ol>
            <li>
              <strong>Go to Staff Management</strong>
            </li>
            <li>
              <strong>Click on a staff member&apos;s name</strong>
            </li>
            <li>
              <strong>Select &quot;Manage Bot Assignments&quot;</strong>
            </li>
            <li>
              <strong>Check the bots they should access</strong>
              <p className="mt-1 text-gray-600">
                You can assign multiple bots to one person, or one bot to multiple people.
              </p>
            </li>
            <li>
              <strong>Set permissions for each bot</strong> (see next section)
            </li>
            <li>
              <strong>Save changes</strong>
            </li>
          </ol>

          <h3>Assignment Strategies</h3>
          <div className="my-6 overflow-x-auto">
            <table className="min-w-full border border-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Strategy
                  </th>
                  <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Best For
                  </th>
                  <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Example
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    Department-based
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Clear ownership
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    HR team manages HR Bot, Sales manages Sales Bot
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    Role-based
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Specialized tasks
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Content team updates KB, Support team reviews chat logs
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    Full access
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">Small teams</td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Everyone can access all bots (simpler but less secure)
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Section 5: Setting Granular Permissions */}
        <section id="permissions" className="scroll-mt-24">
          <h2>5. Setting Granular Permissions</h2>

          <p>
            Each bot assignment comes with granular permissions. This lets you control exactly what
            each team member can do.
          </p>

          <h3>Permission Categories</h3>
          <div className="my-6 space-y-4">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="flex items-center gap-2 font-semibold text-gray-900">
                <Icon icon="tabler:messages" className="text-blue-500" />
                Chat Logs
              </h4>
              <p className="mt-1 text-sm text-gray-600">
                View conversation history, export logs, manage chat sessions.
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">View</span>
                <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">Add</span>
                <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">Edit</span>
                <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                  Delete
                </span>
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="flex items-center gap-2 font-semibold text-gray-900">
                <Icon icon="tabler:database" className="text-green-500" />
                Knowledge Base
              </h4>
              <p className="mt-1 text-sm text-gray-600">
                Upload documents, crawl websites, manage training data sources.
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">View</span>
                <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">Add</span>
                <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">Edit</span>
                <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                  Delete
                </span>
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="flex items-center gap-2 font-semibold text-gray-900">
                <Icon icon="tabler:address-book" className="text-purple-500" />
                Manage Leads
              </h4>
              <p className="mt-1 text-sm text-gray-600">
                View and export lead information collected through the chatbot.
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">View</span>
                <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">Add</span>
                <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">Edit</span>
                <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                  Delete
                </span>
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="flex items-center gap-2 font-semibold text-gray-900">
                <Icon icon="tabler:help" className="text-amber-500" />
                Q&A Training
              </h4>
              <p className="mt-1 text-sm text-gray-600">
                Add custom question-answer pairs to improve bot responses.
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">View</span>
                <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">Add</span>
                <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">Edit</span>
                <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                  Delete
                </span>
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="flex items-center gap-2 font-semibold text-gray-900">
                <Icon icon="tabler:adjustments" className="text-red-500" />
                Tune Bot
              </h4>
              <p className="mt-1 text-sm text-gray-600">
                Modify system prompt, AI model settings, and response behavior.
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">View</span>
                <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">Add</span>
                <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">Edit</span>
                <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                  Delete
                </span>
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="flex items-center gap-2 font-semibold text-gray-900">
                <Icon icon="tabler:palette" className="text-pink-500" />
                Appearance
              </h4>
              <p className="mt-1 text-sm text-gray-600">
                Change colors, avatar, welcome message, and visual styling.
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">View</span>
                <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">Add</span>
                <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">Edit</span>
                <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                  Delete
                </span>
              </div>
            </div>
          </div>

          <h3>Common Permission Templates</h3>
          <div className="my-6 overflow-x-auto">
            <table className="min-w-full border border-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Role
                  </th>
                  <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Recommended Permissions
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    Content Manager
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Knowledge Base (full), Q&A (full), Chat Logs (view only)
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    Support Agent
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Chat Logs (full), Leads (view), Q&A (add only)
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    Designer
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Appearance (full), everything else (view only)
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    Analyst
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Chat Logs (view), Leads (view), everything else (none)
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Section 6: Bulk Operations */}
        <section id="bulk-operations" className="scroll-mt-24">
          <h2>6. Bulk Operations</h2>

          <p>
            When managing multiple bots, bulk operations save time by letting you update several
            bots at once.
          </p>

          <h3>Available Bulk Actions</h3>
          <div className="my-6 space-y-4">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-green-100">
                  <Icon icon="tabler:power" className="text-lg text-green-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Bulk Activate/Deactivate</h4>
                  <p className="mt-1 text-sm text-gray-600">
                    Turn multiple bots on or off at once. Useful for scheduled maintenance or
                    seasonal campaigns.
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100">
                  <Icon icon="tabler:eye" className="text-lg text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Bulk Visibility Change</h4>
                  <p className="mt-1 text-sm text-gray-600">
                    Switch multiple bots between public and private. Both count toward your total
                    chatbot limit. Starter has no separate private bot quota.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <h3>How to Use Bulk Actions</h3>
          <ol>
            <li>
              <strong>Go to your Dashboard</strong>
            </li>
            <li>
              <strong>Select multiple bots</strong>
              <p className="mt-1 text-gray-600">
                Use the checkboxes next to each bot card, or click &quot;Select All&quot;.
              </p>
            </li>
            <li>
              <strong>Click &quot;Bulk Actions&quot;</strong>
              <p className="mt-1 text-gray-600">A dropdown appears with available actions.</p>
            </li>
            <li>
              <strong>Choose your action and confirm</strong>
            </li>
          </ol>

          <div className="my-6 rounded-lg border-l-4 border-blue-500 bg-blue-50 p-4">
            <p className="text-sm text-blue-900">
              <strong>Tip:</strong> Use bulk deactivate before making major knowledge base updates.
              This prevents users from seeing incomplete or inconsistent information during the
              update.
            </p>
          </div>
        </section>

        {/* Section 7: Organization Strategies */}
        <section id="organization-strategies" className="scroll-mt-24">
          <h2>7. Organization Strategies</h2>

          <p>Here are proven patterns for organizing multiple bots across your organization.</p>

          <h3>Pattern 1: Department-Based</h3>
          <p>One bot per department, each with dedicated staff and knowledge base.</p>
          <div className="my-4 overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4">
            <pre className="text-sm text-gray-300">
              {`Organization: Acme Corp

HR Bot (Private)
├── Owner: HR Director
├── Staff: HR Team (3 people)
├── Knowledge: Employee handbook, benefits, policies
└── Audience: Internal employees only

Sales Bot (Public)
├── Owner: Sales VP
├── Staff: Sales Team (5 people)
├── Knowledge: Product info, pricing, case studies
└── Audience: Website visitors, prospects

Support Bot (Public)
├── Owner: Support Manager
├── Staff: Support Team (4 people)
├── Knowledge: FAQs, troubleshooting, docs
└── Audience: Customers`}
            </pre>
          </div>

          <h3>Pattern 2: Audience-Based</h3>
          <p>Separate bots for internal vs. external users with different access levels.</p>
          <div className="my-4 overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4">
            <pre className="text-sm text-gray-300">
              {`Organization: TechStartup Inc

Internal Assistant (Private)
├── Staff: All employees
├── Knowledge: Company wiki, HR policies, IT guides
├── Access: Email whitelist (company domain)
└── Contains: Sensitive internal information

Customer Help (Public)
├── Staff: Support team
├── Knowledge: Public docs, FAQs, tutorials
├── Access: Anyone
└── Contains: Customer-safe information only`}
            </pre>
          </div>

          <h3>Pattern 3: Product-Based</h3>
          <p>One bot per product line, each with product-specific knowledge.</p>
          <div className="my-4 overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4">
            <pre className="text-sm text-gray-300">
              {`Organization: SoftwareCo

Product A Support
├── Knowledge: Product A docs, changelogs, tutorials
├── Appearance: Product A branding
└── Deployed: productA.company.com

Product B Support
├── Knowledge: Product B docs, changelogs, tutorials
├── Appearance: Product B branding
└── Deployed: productB.company.com

Enterprise Support (Private)
├── Knowledge: All products + enterprise features
├── Access: Enterprise customers only
└── Deployed: enterprise.company.com`}
            </pre>
          </div>

          <h3>Best Practices</h3>
          <div className="my-6 space-y-3">
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-green-100">
                <Icon icon="tabler:check" className="text-sm text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Use clear naming conventions</h4>
                <p className="mt-1 text-sm text-gray-600">
                  &quot;HR Internal Bot&quot; is clearer than &quot;Bot 3&quot;. Good names help
                  staff find the right bot.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-green-100">
                <Icon icon="tabler:check" className="text-sm text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Document your bot structure</h4>
                <p className="mt-1 text-sm text-gray-600">
                  Keep a simple spreadsheet listing each bot, its purpose, owner, and staff members.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-green-100">
                <Icon icon="tabler:check" className="text-sm text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Assign clear ownership</h4>
                <p className="mt-1 text-sm text-gray-600">
                  Each bot should have one person responsible for its content accuracy and updates.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-green-100">
                <Icon icon="tabler:check" className="text-sm text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Review permissions quarterly</h4>
                <p className="mt-1 text-sm text-gray-600">
                  People change roles. Regularly audit who has access to what and remove stale
                  assignments.
                </p>
              </div>
            </div>
          </div>

          <div className="my-8 rounded-xl border border-primary-200 bg-primary-50 p-6">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary-100">
                <Icon icon="tabler:bulb" className="text-xl text-primary-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary-900">Pro Tip</h4>
                <p className="mt-1 text-primary-800">
                  Start simple. It&apos;s better to have 2-3 well-maintained bots than 10 neglected
                  ones. You can always add more as your team grows and processes mature.
                </p>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <h3>Next Steps</h3>
          <div className="my-6 grid gap-4 sm:grid-cols-2">
            <Link
              href={PageLink.docsApiReference}
              className="group rounded-lg border border-gray-200 bg-white p-6 transition-all hover:border-primary-200 hover:shadow-md">
              <Icon
                icon="tabler:code"
                className="text-2xl text-gray-400 group-hover:text-primary-500"
              />
              <h4 className="mt-3 font-semibold text-gray-900">API Documentation</h4>
              <p className="mt-1 text-sm text-gray-600">Automate bot management with our API.</p>
            </Link>
            <Link
              href={PageLink.docsEnterpriseSso}
              className="group rounded-lg border border-gray-200 bg-white p-6 transition-all hover:border-primary-200 hover:shadow-md">
              <Icon
                icon="tabler:lock"
                className="text-2xl text-gray-400 group-hover:text-primary-500"
              />
              <h4 className="mt-3 font-semibold text-gray-900">Enterprise SSO Setup</h4>
              <p className="mt-1 text-sm text-gray-600">
                Streamline team access with single sign-on.
              </p>
            </Link>
          </div>
        </section>
      </DocsArticleLayout>
    </>
  );
};

export const getServerSideProps = async () => {
  return { props: {} };
};

export default MultiBotOrchestrationPage;
